/**
 * Face landmark zones (MediaPipe Face Landmarker, 478-point mesh).
 * Each makeup category maps to ordered landmark sequences that the AR
 * coach renders as immersive soft fills or strokes that follow the
 * user's face in real time.
 *
 * Each zone may include:
 *   • paths    — closed polygons or smooth strokes (the soft halo)
 *   • markers  — breathing "tap here" circles at specific landmarks,
 *                so steps that share a face zone (e.g. eyeliner vs
 *                mascara) still read as visually distinct.
 *   • motion   — semantic hint for the renderer (stipple, sweep, swirl,
 *                tap, wiggle, feather, trace).
 */

const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397,
  365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58,
  132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
];

// Dedicated forehead cap. Rendering this alongside FACE_OVAL prevents the
// full-face wash from appearing to cut off above the brows as the head tilts.
const FOREHEAD_CAP = [
  127, 162, 21, 54, 103, 67, 109, 10, 338, 297, 332, 284, 251, 389, 356,
  300, 293, 334, 296, 336, 9, 107, 66, 105, 63, 70,
];

// Forehead band (top of T-zone)
const FOREHEAD_BAND = [
  103, 67, 109, 10, 338, 297, 332,
  336, 285, 8, 55, 107,
];

// Nose bridge + tip strip (bottom of T-zone)
const NOSE_STRIP = [168, 6, 197, 195, 5, 4, 1, 19, 94, 2];

// Lip outer ring
const LIPS_OUTER = [
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
  267, 0, 37, 39, 40, 185,
];

// Lip inner ring (centre of lips — for gloss)
const LIPS_INNER = [
  78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310, 311,
  312, 13, 82, 81, 80, 191,
];

export const LANDMARK_ZONES = {
  primer: {
    label: "All over",
    description: "Press & pat — outward from the centre.",
    motion: "stipple",
    type: "fill",
    paths: [{ indices: FACE_OVAL }, { indices: FOREHEAD_CAP }],
    // Stipple-pat dots radiating from forehead → cheeks → chin
    markers: [10, 151, 9, 8, 50, 280, 234, 454, 152, 195],
  },
  foundation: {
    label: "All over",
    description: "Stipple from the centre — blend outward.",
    motion: "stipple",
    flow: "outward",
    type: "fill",
    paths: [{ indices: FACE_OVAL }, { indices: FOREHEAD_CAP }],
    markers: [9, 50, 280, 152],  // four anchor dots: bridge + cheeks + chin
  },
  concealer: {
    label: "Under the eyes",
    description:
      "Place three tiny dots beneath each eye, then tap from the inner corner outward. Never rub.",
    motion: "tap",
    type: "markers",
    markerStyle: "sparkle",
    paths: [],
    // Three tracked sparkles below each eye: inner, middle, outer.
    markers: [110, 23, 26, 339, 253, 256],
  },
  setting_powder: {
    label: "T-zone focus",
    description: "Press lightly — forehead + bridge of the nose.",
    motion: "tap",
    type: "fill",
    // T-zone only — visually distinct from full-face primer/foundation
    paths: [
      { indices: FOREHEAD_BAND },
      { indices: NOSE_STRIP },
    ],
    markers: [10, 9, 195, 4],
  },
  color_corrector: {
    // Label kept deliberately generic so it reads correctly whether
    // the LLM places the corrector under the eyes (blue/grey shadows),
    // around the mouth (red/pink tones), or along a beard-shadow
    // patch. The AR overlay points to the specific region; this
    // label is the umbrella term for the technique.
    label: "Color correction",
    // Beard shadow doesn't live in one tidy patch — for many faces it
    // covers the entire lower half: cheeks, mustache, jaw, chin, and
    // the underside of the jawline. The polygon below traces from the
    // right ear, across mid-face at cheek-apple / cupid's-bow height,
    // over to the left ear, and back along the full jaw + chin arc.
    description:
      "Press lightly across the lower face — wherever shadow shows up. " +
      "Never rub or sweep.",
    motion: "tap",
    type: "fill",
    markerStyle: "coverage_pat",
    coverageFlow: "centre_out",
    paths: [
      {
        indices: [
          // ─── Top boundary: R ear → cheek apples → upper lip → L ear
          234, 116, 50, 187, 207, 57, 0, 287, 427, 411, 280, 345, 454,
          // ─── L jaw down to chin
          323, 361, 288, 397, 365, 379, 378, 400, 377, 152,
          // ─── R jaw up to ear (closes the polygon)
          148, 176, 149, 150, 136, 172, 58, 132, 93, 234,
        ],
      },
    ],
    // The continuous perimeter is the placement cue. Individual dots made
    // beard coverage look like isolated blemishes instead of one region.
    markers: [],
  },
  contour: {
    label: "Under cheekbones + jaw",
    description: "Sweep diagonally — temple to mouth corner, then blend up.",
    motion: "sweep",
    type: "stroke",
    paths: [
      // Under-cheek hollows
      { indices: [123, 116, 117, 118, 119, 100, 142], width: 14 },
      { indices: [352, 345, 346, 347, 348, 329, 371], width: 14 },
      // Jaw lines
      { indices: [172, 136, 150, 149, 176, 148, 152], width: 11 },
      { indices: [397, 365, 379, 378, 400, 377, 152], width: 11 },
    ],
  },
  bronzer: {
    label: "Temples + upper cheeks",
    description: "Trace a soft '3' — temple → cheekbone → jaw on each side.",
    motion: "trace",
    type: "stroke",
    paths: [
      { indices: [109, 67, 21, 54, 103, 117, 118, 50], width: 18 },
      { indices: [338, 297, 251, 284, 332, 346, 347, 280], width: 18 },
    ],
  },
  blush: {
    label: "Cheek apples",
    description: "Smile, find the apples, place the color there and circle outward towards the temple.",
    motion: "swirl",
    flow: "temple",
    type: "markers",
    markerStyle: "circularSparkle",
    paths: [],
    // One tracked focal point on each cheek apple.
    markers: [50, 280],
  },
  highlighter: {
    label: "High points",
    description: "Tap onto cheekbones, brow bone, cupid's bow, nose bridge.",
    motion: "tap",
    type: "fill",
    paths: [
      { indices: [50, 205, 207, 36] },     // right cheekbone top
      { indices: [280, 425, 427, 266] },   // left cheekbone top
      { indices: [168, 6, 197, 195, 4] },  // nose bridge
      { indices: [11, 0, 37, 12] },        // cupid's bow
    ],
    // Sparkle dots — exactly where to tap the highlighter
    markers: [50, 280, 9, 6, 11, 105, 334],
  },
  brows: {
    label: "Brow line",
    description: "Feather hair-like strokes — upward & outward.",
    motion: "feather",
    type: "stroke",
    paths: [
      { indices: [70, 63, 105, 66, 107], width: 9 },
      { indices: [336, 296, 334, 293, 300], width: 9 },
    ],
  },
  eyeshadow: {
    label: "Eyelids",
    description: "Sweep across the lid — windshield-wiper motion.",
    motion: "sweep",
    type: "fill",
    paths: [
      // Right lid: upper lid line + crease arc back
      { indices: [33, 246, 161, 160, 159, 158, 157, 173, 133, 190, 56, 28, 27, 29, 30, 247] },
      // Left lid
      { indices: [263, 466, 388, 387, 386, 385, 384, 398, 362, 414, 286, 258, 257, 259, 260, 467] },
    ],
  },
  eyeliner: {
    label: "Lash line",
    description: "Trace from inner corner outward — flick at the tail.",
    motion: "trace",
    type: "stroke",
    paths: [
      { indices: [33, 246, 161, 160, 159, 158, 157, 173, 133], width: 5 },
      { indices: [263, 466, 388, 387, 386, 385, 384, 398, 362], width: 5 },
    ],
    // Inner-corner start + outer-tail flick markers (wing tips)
    markers: [33, 133, 263, 362],
  },
  mascara: {
    label: "Upper lashes",
    description: "Wiggle from root to tip — both sides of the lashes.",
    motion: "wiggle",
    type: "stroke",
    paths: [
      // Slightly above the lash line so the wiggle reads as lashes
      { indices: [33, 246, 161, 160, 159, 158, 157, 173, 133], width: 4 },
      { indices: [263, 466, 388, 387, 386, 385, 384, 398, 362], width: 4 },
    ],
    // Vertical wisp markers above the eyes — clearly different from eyeliner
    markers: [159, 158, 386, 385],
  },
  lip_liner: {
    label: "Lip outline",
    description: "Trace the natural lip line — over-draw lightly to define.",
    motion: "trace",
    type: "stroke",
    paths: [{ indices: [...LIPS_OUTER, LIPS_OUTER[0]], width: 4 }],
  },
  lipstick: {
    label: "Lips",
    description: "Fill from the centre outward to the corners.",
    motion: "sweep",
    type: "fill",
    paths: [{ indices: LIPS_OUTER }],
    markers: [13, 14],  // centre of upper + lower lip
  },
  lip_gloss: {
    label: "Lips · centre",
    description: "Dab on the centre — let it spread for a soft, full pout.",
    motion: "tap",
    type: "fill",
    // Inner ring only — visibly distinct from full lipstick fill
    paths: [{ indices: LIPS_INNER }],
    markers: [13, 14],
  },
};

export const getZone = (category) =>
  LANDMARK_ZONES[category] || {
    label: "Apply gently",
    description: "Follow the routine guidance for this step.",
    motion: "tap",
    type: "fill",
    paths: [{ indices: FACE_OVAL }],
    markers: [],
  };

// ─── Where-aware sub-zone resolver ────────────────────────────────
// Why this exists
// ──────────────
// Each entry in LANDMARK_ZONES draws the FULL face region for a
// category — e.g. `eyeshadow` lights up the entire upper lid. That's
// correct for "Sweep shadow across the lid" but WRONG for technique
// steps that target a narrower patch ("Build the smoke into the
// crease", "Tap shimmer in the inner corner", "Press color into the
// outer V"). Founder feedback after checking Smoky Eye in production:
// the AR mirror was highlighting the whole lid for every step.
//
// The fix is to inspect `step.where` for known sub-region keywords
// and return a tighter polygon when one matches. We deliberately
// keep the keyword list short and unambiguous — false-positive
// narrowing is much worse than no narrowing at all (a wrong patch
// is more confusing than a correct big patch).
//
// MediaPipe landmark cheat sheet for the polygons below:
//   • Right eye:  33 (outer corner), 133 (inner corner),
//                 159 (upper-mid), 145 (lower-mid)
//   • Left eye:   263 (outer corner), 362 (inner corner),
//                 386 (upper-mid), 374 (lower-mid)
//   • Lip centre: 13 (upper inner), 14 (lower inner)
//   • Lip corners: 61 (right), 291 (left)
//   • Cupid's bow ring: 0 (centre), 11 (peak), 37/267 (peaks)

// Tight crease ribbons — narrow band running just above the lid line
// where the natural crease folds. Used for "in the crease", "build
// the smoke", etc.
const CREASE_RIGHT = [
  // Slightly higher than upper lash line; runs from inner to outer corner.
  133, 173, 157, 158, 159, 160, 161, 246, 33,
];
const CREASE_LEFT = [
  362, 398, 384, 385, 386, 387, 388, 466, 263,
];

// Outer V — outer third of the lid only. The dark "smudge" zone for
// smoky-eye outer-V steps.
const OUTER_V_RIGHT = [33, 246, 161, 160, 159, 130, 247];
const OUTER_V_LEFT = [263, 466, 388, 387, 386, 359, 467];

// Inner corner highlight zone — pearly tap spot for "lift the inner
// corner" type steps.
const INNER_CORNER_RIGHT = [133, 173, 190, 56];
const INNER_CORNER_LEFT = [362, 398, 414, 286];

// Lower lash line — pencil/shadow smudge along the bottom waterline.
const LOWER_LASH_RIGHT = [33, 7, 163, 144, 145, 153, 154, 155, 133];
const LOWER_LASH_LEFT = [263, 249, 390, 373, 374, 380, 381, 382, 362];

// Brow bone (under-brow highlight zone).
const BROW_BONE_RIGHT = [70, 63, 105, 66, 107, 55, 8];
const BROW_BONE_LEFT = [336, 296, 334, 293, 300, 285, 8];

// Inner brow head only (for "lift the inner brow" type steps).
const INNER_BROW_HEAD = [107, 55, 8, 285, 336];

// Brow tail only (for "extend the tail" type steps).
const BROW_TAIL_RIGHT = [70, 63];
const BROW_TAIL_LEFT = [336, 296];

// Lip centre only — the cushion of the bottom lip / cupid's bow peak.
// For "tap gloss in the centre" type steps.
const LIP_CENTRE = [13, 14, 78, 308, 191, 80, 81, 82, 13];
const CUPIDS_BOW = [11, 0, 37, 267, 12];

// ─── Sub-zone keyword table ──────────────────────────────────────
// Order matters: we test in declaration order so more-specific
// keywords beat broader ones ("outer corner of the eye" should hit
// OUTER_V, not CREASE).
const SUB_ZONE_RULES = [
  {
    category: "eyeshadow",
    keywords: ["outer v", "outer corner", "outer third", "outer-v"],
    zone: {
      label: "Outer V",
      description: "Press color into the outer V — build with passes.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: OUTER_V_RIGHT }, { indices: OUTER_V_LEFT }],
      markers: [33, 263],
    },
  },
  {
    category: "eyeshadow",
    keywords: [
      "inner corner", "tear duct", "tear ducts", "lift the inner",
      "pearly inner", "champagne inner",
    ],
    zone: {
      label: "Inner corner",
      description: "Tap a pearl-light shade into the inner corner.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: INNER_CORNER_RIGHT }, { indices: INNER_CORNER_LEFT }],
      markers: [133, 362],
    },
  },
  {
    category: "eyeshadow",
    keywords: ["crease", "in the crease", "build the smoke", "smoky", "transition shade"],
    zone: {
      label: "Crease",
      description: "Buff color into the crease — small circles.",
      motion: "swirl",
      type: "stroke",
      paths: [
        { indices: CREASE_RIGHT, width: 7 },
        { indices: CREASE_LEFT, width: 7 },
      ],
      markers: [159, 386],
    },
  },
  {
    category: "eyeshadow",
    keywords: ["brow bone", "browbone", "under the brow", "under brow"],
    zone: {
      label: "Brow bone",
      description: "Highlight the brow bone — soft tap.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: BROW_BONE_RIGHT }, { indices: BROW_BONE_LEFT }],
      markers: [105, 334],
    },
  },
  {
    category: "eyeshadow",
    keywords: ["lower lash", "lower waterline", "smudge the lower", "under the eye"],
    zone: {
      label: "Lower lash line",
      description: "Smudge color along the lower lash line.",
      motion: "trace",
      type: "stroke",
      paths: [
        { indices: LOWER_LASH_RIGHT, width: 4 },
        { indices: LOWER_LASH_LEFT, width: 4 },
      ],
      markers: [145, 374],
    },
  },
  // Eyeliner specifics
  {
    category: "eyeliner",
    keywords: ["tightline", "between the lashes", "lash root", "press into the lash"],
    zone: {
      label: "Tightline",
      description: "Press color RIGHT into the lash root — no skin gap.",
      motion: "tap",
      type: "stroke",
      paths: [
        // Slightly tighter run than the full eyeliner zone — just
        // the centre of the lash line, where tightlining lives.
        { indices: [161, 160, 159, 158, 157], width: 3 },
        { indices: [388, 387, 386, 385, 384], width: 3 },
      ],
      markers: [159, 386],
    },
  },
  {
    category: "eyeliner",
    keywords: ["lower lash", "lower liner", "lower waterline"],
    zone: {
      label: "Lower lash line",
      description: "Trace the lower lash line — keep it soft.",
      motion: "trace",
      type: "stroke",
      paths: [
        { indices: LOWER_LASH_RIGHT, width: 4 },
        { indices: LOWER_LASH_LEFT, width: 4 },
      ],
      markers: [145, 374],
    },
  },
  // Brow specifics
  {
    category: "brows",
    keywords: ["inner brow", "brow head", "inner head"],
    zone: {
      label: "Inner brow head",
      description: "Feather light strokes up and out — keep it soft.",
      motion: "feather",
      type: "fill",
      paths: [{ indices: INNER_BROW_HEAD }],
      markers: [107, 336],
    },
  },
  {
    category: "brows",
    keywords: ["brow tail", "the tail", "extend the tail", "outer brow"],
    zone: {
      label: "Brow tail",
      description: "Lift the tail — angled up and out.",
      motion: "feather",
      type: "stroke",
      paths: [
        { indices: BROW_TAIL_RIGHT, width: 7 },
        { indices: BROW_TAIL_LEFT, width: 7 },
      ],
      markers: [70, 336],
    },
  },
  // Lip specifics
  {
    category: "lipstick",
    keywords: ["centre of the lip", "center of the lip", "centre of your lip", "center of your lip"],
    zone: {
      label: "Lip centre",
      description: "Apply from the centre — let it spread.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: LIP_CENTRE }],
      markers: [13, 14],
    },
  },
  {
    category: "lipstick",
    keywords: ["cupid's bow", "cupids bow", "cupid bow"],
    zone: {
      label: "Cupid's bow",
      description: "Define the bow — sharper edge on top.",
      motion: "trace",
      type: "fill",
      paths: [{ indices: CUPIDS_BOW }],
      markers: [11, 0],
    },
  },
  {
    category: "lip_liner",
    keywords: ["cupid's bow", "cupids bow", "cupid bow"],
    zone: {
      label: "Cupid's bow",
      description: "Sharpen the peaks of the cupid's bow.",
      motion: "trace",
      type: "stroke",
      paths: [{ indices: CUPIDS_BOW, width: 3 }],
      markers: [11, 0],
    },
  },
  {
    category: "lip_gloss",
    keywords: ["centre", "center", "bottom lip", "cushion"],
    zone: {
      label: "Lip centre",
      description: "Dab on the centre — let it spread.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: LIP_CENTRE }],
      markers: [13, 14],
    },
  },
  // Highlighter specifics — already a multi-spot tap but the founder
  // may write a step like "tap on cupid's bow only".
  {
    category: "highlighter",
    keywords: ["cupid's bow", "cupids bow", "cupid bow"],
    zone: {
      label: "Cupid's bow",
      description: "A whisper of light on the cupid's bow.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: CUPIDS_BOW }],
      markers: [11, 0],
    },
  },
  {
    category: "highlighter",
    keywords: ["inner corner", "inner eye"],
    zone: {
      label: "Inner corner",
      description: "A pearl tap in the inner corner.",
      motion: "tap",
      type: "fill",
      paths: [{ indices: INNER_CORNER_RIGHT }, { indices: INNER_CORNER_LEFT }],
      markers: [133, 362],
    },
  },
];

/**
 * Where-aware zone resolver. Pass the step's `where` text and we'll
 * narrow the highlighted region to a sub-zone when the text matches
 * a known anatomical patch. Falls back to the full-category zone
 * when no match is found (so steps without specific placement
 * guidance still render correctly).
 *
 * Optionally pass the step's `title` so we can repair mis-categorized
 * steps coming from the LLM. The most common drift we've seen: the
 * LLM emits `category: "primer"` (or `"foundation"`) for a step
 * titled "Wake Up the Eyes" with `where: "under the eyes"`. Without
 * this repair, the AR mirror would label the step "All over" and
 * draw the full-face polygon — visibly disconnected from the spoken
 * instruction. The repair only fires when the category is plausibly
 * a generic full-face one (primer / foundation / setting_powder)
 * AND the title or where clearly says "under the eyes" — so it
 * cannot mistakenly steal an eyeshadow / brows / lips step.
 *
 * Always returns a Zone object — never null — so callers can use it
 * unconditionally.
 */
const CONCEALER_TITLE_PATTERNS = [
  /\bwake\s+(up\s+)?(the\s+|your\s+)?eyes?\b/i,
  /\bbrighten(ing)?\s+(the\s+|your\s+)?(under[-\s]?eyes?|eyes?)\b/i,
  /\bcatch(es|ing)?\s+the\s+light\b/i,
  /\bunder[-\s]?eye\s+(triangle|brighten|wake)/i,
];
const CONCEALER_WHERE_PATTERNS = [
  /\bunder\s+(the\s+|your\s+)?eyes?\b/i,
  /\bunder[-\s]?eye\s+(triangle|area)\b/i,
  /\btear\s+trough\b/i,
];
const PROMOTABLE_TO_CONCEALER = new Set([
  // These are the "all over" / generic categories that the LLM tends
  // to fall back to when it can't decide where a brightening step
  // belongs. eyeshadow / mascara / blush etc. are intentionally NOT
  // in this set — a "under the eye" eyeshadow smudge step is a real
  // thing and must keep its own zone.
  "primer",
  "foundation",
  "setting_powder",
]);

const BROW_GUIDES = {
  map: {
    browMode: "map",
    description:
      "Find three quiet guide points: the brow head, its natural lift, and the tail. These are landmarks, not an outline.",
  },
  hair_strokes: {
    browMode: "hair_strokes",
    description:
      "Touch, flick, lift. Add short, separate strokes only where the brow feels sparse, following the natural hair direction.",
  },
  blend: {
    browMode: "blend",
    description:
      "Comb from the inner brow upward and outward. This softens strong marks and blends them into your natural hairs.",
  },
  pomade: {
    browMode: "pomade",
    description:
      "Use just a little product. Define beneath the arch and tail, then feather what remains softly through the brow.",
  },
  gel: {
    browMode: "gel",
    description:
      "Wiggle gently at the roots, brush upward, then guide the hairs outward toward the tail.",
  },
  wax: {
    browMode: "wax",
    description:
      "Press the hairs upward from root to tip, then shape them outward through the arch and tail.",
  },
};

const getBrowGuide = (whereText, titleText, whatText) => {
  const text = `${titleText || ""} ${whereText || ""} ${whatText || ""}`.toLowerCase();
  let guide;
  if (/\b(find|map|guide points?|three quiet|natural arch)\b/.test(text)) {
    guide = BROW_GUIDES.map;
  } else if (/\b(wax|laminat|brow freeze)\b/.test(text)) {
    guide = BROW_GUIDES.wax;
  } else if (/\b(spoolie|blend|soften|comb through|comb from)\b/.test(text)) {
    guide = BROW_GUIDES.blend;
  } else if (/\b(pomade|dipbrow|lower edge|outline)\b/.test(text)) {
    guide = BROW_GUIDES.pomade;
  } else if (/\b(clear gel|tinted gel|brow gel|wand)\b/.test(text)) {
    guide = BROW_GUIDES.gel;
  } else if (/\b(pencil|microfill|brow pen|hair-like|hair like|fill)\b/.test(text)) {
    guide = BROW_GUIDES.hair_strokes;
  }
  return guide
    ? {
        ...LANDMARK_ZONES.brows,
        // Product-specific brow coaching replaces—not layers over—the
        // original filled brow ribbon and its legacy markers.
        paths: [],
        markers: [],
        ...guide,
      }
    : LANDMARK_ZONES.brows;
};

export const getZoneForStep = (category, whereText, titleText, whatText) => {
  const text = (whereText || "").toLowerCase();
  const title = (titleText || "").toLowerCase();

  // ── Defensive net: promote mis-categorized "wake the eyes" /
  // "brighten the under-eyes" steps to the concealer zone so the AR
  // mirror highlights the under-eye triangle (not the full face).
  if (PROMOTABLE_TO_CONCEALER.has(category)) {
    const titleSaysConcealer = CONCEALER_TITLE_PATTERNS.some((re) =>
      re.test(title),
    );
    const whereSaysConcealer = CONCEALER_WHERE_PATTERNS.some((re) =>
      re.test(text),
    );
    if (titleSaysConcealer || whereSaysConcealer) {
      return LANDMARK_ZONES.concealer;
    }
  }

  if (text) {
    for (const rule of SUB_ZONE_RULES) {
      if (rule.category !== category) continue;
      if (rule.keywords.some((kw) => text.includes(kw))) {
        if (category === "brows") {
          const guide = getBrowGuide(whereText, titleText, whatText);
          return guide === LANDMARK_ZONES.brows
            ? rule.zone
            : {
                ...rule.zone,
                paths: [],
                markers: [],
                browMode: guide.browMode,
                description: guide.description,
              };
        }
        return rule.zone;
      }
    }
  }
  if (category === "brows") {
    return getBrowGuide(whereText, titleText, whatText);
  }
  return getZone(category);
};

/**
 * Per-user shadow-zone catalog. Each entry is a circular blob anchored
 * at one MediaPipe landmark, with radius expressed as a fraction of
 * face width (computed from landmarks 234 ↔ 454). Source of truth lives
 * server-side in `/app/backend/shadow_zones.py`; this map is the
 * front-end mirror used by the AR mirror to render only the zones the
 * user has selected.
 */
export const SHADOW_ZONE_CATALOG = {
  mustache:    { center: 164, radius: 0.13 },
  upper_lip:   { center: 0,   radius: 0.10 },
  cheek_r:     { center: 192, radius: 0.13 },
  cheek_l:     { center: 416, radius: 0.13 },
  jowl_r:      { center: 138, radius: 0.12 },
  jowl_l:      { center: 367, radius: 0.12 },
  jaw_r:       { center: 172, radius: 0.10 },
  jaw_l:       { center: 397, radius: 0.10 },
  chin:        { center: 152, radius: 0.12 },
  under_chin:  { center: 18,  radius: 0.13 },
};

/**
 * Build the zone object the AR mirror should render for the
 * color-corrector step. If the user has saved one or more shadow
 * zones, return one perimeter enclosing all selected areas. Otherwise
 * fall back to the default full-lower-face polygon
 * (the existing LANDMARK_ZONES.color_corrector behaviour).
 *
 * @param {string[]|null|undefined} selectedZoneIds
 */
export const getColorCorrectorZone = (selectedZoneIds) => {
  const valid = Array.isArray(selectedZoneIds)
    ? selectedZoneIds.filter((id) => SHADOW_ZONE_CATALOG[id])
    : [];
  if (valid.length === 0) {
    return LANDMARK_ZONES.color_corrector;
  }
  return {
    label: "Soften your shadow zones",
    description:
      "Press lightly on each highlighted area — only where shadow lives for you.",
    motion: "tap",
    type: "perimeter",
    markerStyle: "coverage_pat",
    coverageFlow: "centre_out",
    blobs: valid.map((id) => ({ id, ...SHADOW_ZONE_CATALOG[id] })),
    markers: [],
  };
};

/**
 * Face-shape-adaptive contour strokes.
 *
 * Classic contour theory: the face is "balanced" toward an oval, so
 * each non-oval shape gets strokes designed to optically pull the
 * widest point smaller and fill out the narrowest point. The
 * landmark indices below trace the canonical placements beauty
 * editors publish for each shape.
 *
 * The shape is detected in-browser by `classifyFaceShape` in
 * `/app/frontend/src/lib/faceShape.js`. If the classifier returns
 * null (no landmarks yet / head turned away / low confidence
 * pre-smoothing), `getContourZone` falls back to oval — the
 * balanced baseline that reads well on almost every face.
 */
export const CONTOUR_BY_SHAPE = {
  // OVAL — balanced; lightly define under-cheek + jawline. Same as
  // LANDMARK_ZONES.contour, kept here so every branch returns the
  // same zone-object shape.
  oval: {
    label: "Under cheekbones + jaw",
    description:
      "Oval face — sweep diagonally from temple to mouth corner, then blend up.",
    motion: "sweep",
    type: "stroke",
    paths: [
      { indices: [123, 116, 117, 118, 119, 100, 142], width: 14 },
      { indices: [352, 345, 346, 347, 348, 329, 371], width: 14 },
      { indices: [172, 136, 150, 149, 176, 148, 152], width: 11 },
      { indices: [397, 365, 379, 378, 400, 377, 152], width: 11 },
    ],
  },

  // ROUND — elongate. Stronger vertical strokes down the sides of
  // the face, less emphasis on the jaw. Darkens the temples + side
  // cheeks so the face reads longer.
  round: {
    label: "Sides + temples",
    description:
      "Round face — sweep vertically from temple to lower cheek to lengthen.",
    motion: "sweep",
    type: "stroke",
    paths: [
      // Vertical temple-to-cheek side strokes (elongating)
      { indices: [54, 103, 234, 127, 93, 132, 58], width: 16 },
      { indices: [284, 332, 454, 356, 323, 361, 288], width: 16 },
      // Light under-cheek accent
      { indices: [123, 116, 117, 118, 119], width: 11 },
      { indices: [352, 345, 346, 347, 348], width: 11 },
    ],
  },

  // HEART — balance the broader forehead and narrow pointed chin.
  // Soft contour at temples (narrows top) + very light jaw work
  // (keep the dainty chin). Cheeks get a standard diagonal sweep.
  heart: {
    label: "Temples + under-cheek",
    description:
      "Heart face — soften temples first, then a diagonal sweep under each cheekbone.",
    motion: "sweep",
    type: "stroke",
    paths: [
      // Temple softening (narrows the wider forehead)
      { indices: [103, 67, 109, 10, 338, 297, 332], width: 13 },
      // Standard under-cheek sweep
      { indices: [123, 116, 117, 118, 119, 100, 142], width: 14 },
      { indices: [352, 345, 346, 347, 348, 329, 371], width: 14 },
    ],
  },

  // LONG — shorten. Horizontal contour at the forehead hairline AND
  // under the chin pulls the face visually shorter. Skip the long
  // diagonal jaw sweep (would lengthen further).
  long: {
    label: "Hairline + under-chin",
    description:
      "Long face — sweep horizontally across the hairline and under the chin to shorten.",
    motion: "sweep",
    type: "stroke",
    paths: [
      // Forehead horizontal band (shortens)
      { indices: [103, 67, 109, 10, 338, 297, 332], width: 16 },
      // Under-chin horizontal band (shortens)
      { indices: [172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365, 397], width: 13 },
      // Light under-cheek accent, short stroke only
      { indices: [116, 117, 118], width: 10 },
      { indices: [345, 346, 347], width: 10 },
    ],
  },

  // SQUARE — soften the angular jaw. Focus on the gonial angle with
  // a curved stroke that reads as "rounded" rather than sharp.
  square: {
    label: "Jaw corners + under-cheek",
    description:
      "Square face — curve the contour around the jaw corners to soften the angle.",
    motion: "sweep",
    type: "stroke",
    paths: [
      // Jaw-corner softening (rounds the angle)
      { indices: [58, 172, 136, 150], width: 16 },
      { indices: [288, 397, 365, 379], width: 16 },
      // Standard under-cheek diagonal
      { indices: [123, 116, 117, 118, 119, 100], width: 13 },
      { indices: [352, 345, 346, 347, 348, 329], width: 13 },
    ],
  },

  // DIAMOND — cheekbones are widest. Lightly narrow the cheek peaks
  // with a short stroke directly on the zygomatic arch; add gentle
  // fill on forehead + jaw to broaden them visually.
  diamond: {
    label: "Cheek peaks",
    description:
      "Diamond face — short stroke along the cheek peak to narrow, soft shading on forehead + jaw.",
    motion: "sweep",
    type: "stroke",
    paths: [
      // Cheekbone peak narrowing (short + concentrated)
      { indices: [234, 127, 93], width: 18 },
      { indices: [454, 356, 323], width: 18 },
      // Gentle forehead band (widens optically)
      { indices: [67, 109, 10, 338, 297], width: 10 },
      // Gentle jaw line (widens optically)
      { indices: [172, 136, 150, 149], width: 10 },
      { indices: [397, 365, 379, 378], width: 10 },
    ],
  },
};

/**
 * Resolve the contour zone to render. Pass the shape string from
 * `classifyFaceShape(...).shape` (one of oval / round / heart / long
 * / square / diamond). Unknown / null / undefined falls back to
 * `CONTOUR_BY_SHAPE.oval`.
 */
export const getContourZone = (shape) => {
  if (shape && CONTOUR_BY_SHAPE[shape]) return CONTOUR_BY_SHAPE[shape];
  return CONTOUR_BY_SHAPE.oval;
};
